/**
 * What a voice played, cut into stretches of time and then into bars.
 *
 * This is the half of transcription that has no opinions: it turns a list of notes with
 * ticks into a list of stretches through which the same keys are sounding, and then cuts
 * those stretches at the bar lines. Nothing here knows about note values, keys or staves,
 * which is what keeps {@link transcribe} readable -- and what makes the one genuinely
 * awkward rule easy to state: a voice shows one thing at a time, so where one of its notes
 * is still sounding when another is struck or let go, the stretch ends there and the note
 * is tied into the next one.
 */

/** A note of one track, already scaled to the editor's ticks and snapped to the grid. */
export interface ScaledNote {
  readonly key: number;
  readonly start: number;
  readonly ticks: number;
  /**
   * Let go off the grid, as a hand does and a written file does not: its length is then a
   * reading of a performance, and a grid step of it either way is not taken at its word.
   */
  readonly played?: boolean;
}

/** A stretch of one voice's time: the notes sounding through it, or none, for a rest. */
export interface Span {
  readonly start: number;
  readonly ticks: number;
  readonly notes: readonly ScaledNote[];
}

/** One head of a piece: a key, and whether it is held from before or on into what follows. */
export interface Head {
  readonly key: number;
  readonly tiedFrom: boolean;
  readonly tiedTo: boolean;
  /** The bar the note was struck in, which is the bar its head is spelled in. */
  readonly struck: number;
}

/** As much of a span as falls inside one bar. */
export interface Piece {
  readonly ticks: number;
  readonly heads: readonly Head[];
}

/** The span of time one bar covers. */
export interface BarSpan {
  readonly start: number;
  readonly ticks: number;
}

/** The bar a tick falls in. */
export function barAt(bars: readonly BarSpan[], tick: number): number {
  let low = 0;
  let high = bars.length - 1;
  while (low < high) {
    const half = Math.ceil((low + high) / 2);
    if ((bars[half] as BarSpan).start <= tick) low = half;
    else high = half - 1;
  }
  return low;
}

/**
 * The notes with every one let go by the next attack, those struck together as long as the
 * longest of them: what a staff holds where nothing is tied, which is a drum staff.
 */
export function clipped(notes: readonly ScaledNote[]): readonly ScaledNote[] {
  const longest = new Map<number, number>();
  for (const note of notes) {
    longest.set(note.start, Math.max(longest.get(note.start) ?? 0, note.ticks));
  }
  const onsets = [...longest.keys()].sort((first, second) => first - second);
  const until = new Map<number, number>();
  onsets.forEach((at, index) => {
    const next = onsets[index + 1] ?? Infinity;
    until.set(at, Math.min(longest.get(at) ?? 0, next - at));
  });
  return notes.map((note) => ({ ...note, ticks: until.get(note.start) ?? note.ticks }));
}

/**
 * The notes as a keyboard would sound them: a key struck again while it is still down is
 * let go there, and of one key struck twice at once the longer is kept. A note shorter than
 * the grid still lasts `unit`, since a written note has to be long enough to draw.
 */
function sounded(notes: readonly ScaledNote[], until: number, unit: number): ScaledNote[] {
  const ordered = [...notes].sort(
    (first, second) => first.start - second.start || second.ticks - first.ticks,
  );
  const down = new Map<number, number>();
  const out: ScaledNote[] = [];
  for (const note of ordered) {
    const before = down.get(note.key);
    const last = before === undefined ? undefined : out[before];
    if (before !== undefined && last !== undefined) {
      if (last.start === note.start) continue;
      if (last.start + last.ticks > note.start) {
        out[before] = { ...last, ticks: note.start - last.start };
      }
    }
    const ticks = Math.min(Math.max(unit, note.ticks), until - note.start);
    if (ticks <= 0) continue;
    down.set(note.key, out.length);
    out.push({ ...note, ticks });
  }
  return out;
}

/**
 * The stretches one voice's notes make, from tick zero to `until`, with no gaps.
 *
 * A stretch ends wherever a note of the voice is struck or let go, so the same notes sound
 * all the way through it: notes struck together are a chord, and a note held while another
 * comes or goes is in the stretch on both sides of that, which is what a tie writes.
 */
export function spansOf(
  notes: readonly ScaledNote[],
  until: number,
  unit: number,
): readonly Span[] {
  const played = sounded(notes, until, unit);
  const edges = new Set<number>([0, until]);
  for (const note of played) {
    edges.add(note.start);
    edges.add(note.start + note.ticks);
  }
  const cuts = [...edges].sort((first, second) => first - second);

  const spans: Span[] = [];
  let sounding: ScaledNote[] = [];
  let next = 0;
  for (let index = 0; index + 1 < cuts.length; index += 1) {
    const start = cuts[index] as number;
    sounding = sounding.filter((note) => note.start + note.ticks > start);
    while (next < played.length && (played[next] as ScaledNote).start === start) {
      sounding.push(played[next] as ScaledNote);
      next += 1;
    }
    const ordered = [...sounding].sort((first, second) => first.key - second.key);
    spans.push({ start, ticks: (cuts[index + 1] as number) - start, notes: ordered });
  }
  return spans;
}

/**
 * The same spans, cut at the bar lines: one list of pieces for each bar.
 *
 * A note that sounds on past the end of its piece, into the next stretch or over the bar
 * line, has its head tied to the one that follows -- the only way a note longer than a bar
 * can be written at all.
 */
export function piecesOf(
  spans: readonly Span[],
  bars: readonly BarSpan[],
): readonly (readonly Piece[])[] {
  const out: Piece[][] = bars.map(() => []);
  for (const span of spans) {
    let at = span.start;
    let left = span.ticks;
    for (let index = barAt(bars, at); index < bars.length && left > 0; index += 1) {
      const bar = bars[index] as BarSpan;
      const take = Math.min(left, bar.start + bar.ticks - at);
      if (take <= 0) continue;
      const from = at;
      (out[index] as Piece[]).push({
        ticks: take,
        heads: span.notes.map((note) => ({
          key: note.key,
          tiedFrom: note.start < from,
          tiedTo: note.start + note.ticks > from + take,
          struck: barAt(bars, note.start),
        })),
      });
      at += take;
      left -= take;
    }
  }
  return out;
}
